import {
  all,
  call,
  debounce,
  put,
  spawn,
  takeLatest,
} from 'redux-saga/effects';
import request from 'utils/request';

import { showToast } from '../Toast/actions';
import {
  CREATE_VENDOR_BALANCE_TRANSFER,
  CREATE_VENDOR_BALANCE_TRANSFER_URL,
  DEBITED_FROM_MAPPING,
  GENERATE_SETTLEMENT_REPORT,
  GET_ON_DEMAND_TABLE_DATA,
  GET_SCHEDULED_TABLE_DATA,
  GET_SCHEDULED_TABLE_DATA_URL,
  GET_SETTLEMENT_TIMELINE,
  GET_TRANSFER_BALANCE,
  GET_TRANSFER_BALANCE_URL,
  GET_TRANSFER_CHARGES,
  GET_TRANSFER_CHARGES_URL,
  SEARCH_VENDOR_SETTLEMENTS_TIMELINE_URL,
} from './constants';
import { GENERATE_REPORT_URL } from '../../constants/url';
import {
  disableLoaderAction,
  saveTransferBalanceSuccessAction,
} from './actions';

export function* getTransferBalance({ data: { data, cb, errorCb } }) {
  try {
    const { merchantId, vendorId } = data;
    const { url, method } = GET_TRANSFER_BALANCE_URL;
    const updatedUrl = url
      .replace('$merchantId$', merchantId)
      .replace('$vendorId$', vendorId);
    const response = yield call(request, updatedUrl, { method });
    cb(response);
  } catch (err) {
    errorCb(err);
    yield put(showToast(err));
  }
}

export function* getTransferCharges({ data: { data, cb, errorCb } }) {
  try {
    const { merchantId, amount } = data;
    const { url, method } = GET_TRANSFER_CHARGES_URL;
    const updatedUrl = url
      .replace('$merchantId$', merchantId)
      .replace('$amount$', amount);
    const options = {
      method,
      queryString: { rateType: 'VENDOR_ON_DEMAND' },
    };
    const response = yield call(request, updatedUrl, options);
    cb(response);
  } catch (err) {
    errorCb();
    yield put(showToast(err));
  }
}

export function* getOnDemandTableData({ data }) {
  try {
    const { options: reqOptions, cb } = data;
    const { debitedFrom = [], ...filterOptions } = reqOptions;
    let payoutTypes = [];
    if (debitedFrom.length === 1) {
      payoutTypes = DEBITED_FROM_MAPPING[debitedFrom[0]];
    } else {
      payoutTypes = ['MERCHANT_ON_DEMAND', 'VENDOR_ON_DEMAND', 'ON_DEMAND'];
    }
    const { url, method } = GET_SCHEDULED_TABLE_DATA_URL;
    const options = {
      method,
      queryString:
        {
          ...filterOptions,
          payoutTypes,
        } || {},
    };
    const response = yield call(request, url, options);

    if (cb) cb(response);
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* getScheduledTableData({ data }) {
  try {
    const { options: reqOptions, cb } = data;

    const { url, method } = GET_SCHEDULED_TABLE_DATA_URL;
    const options = {
      method,
      queryString: reqOptions || {},
    };
    const response = yield call(request, url, options);
    if (cb) cb(response);
  } catch (err) {
    yield put(disableLoaderAction());
    yield put(showToast(err));
  }
}

export function* generateSettlementReport({ data }) {
  const { cb, actions, ...reqOptions } = data.options;

  try {
    const { url, method } = GENERATE_REPORT_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,

      queryString: reqOptions || {},
    };
    const response = yield call(request, url, options);
    yield put(disableLoaderAction());
    if (cb) cb(response);
  } catch (err) {
    if (actions) actions.setSubmitting(false);
    yield put(disableLoaderAction());
    yield put(showToast(err));
    if (cb) {
      cb({ error: err });
    }
  }
}

export function* createVendorBalanceTransfer({
  data: { payload, actions, merchantId, cb },
}) {
  try {
    const { url, method } = CREATE_VENDOR_BALANCE_TRANSFER_URL;
    const updatedUrl = url
      .replace('$merchantId$', merchantId)
      .replace('$vendorId$', payload.vendorId);
    const options = {
      method,
      body: JSON.stringify(payload),
    };
    const response = yield call(request, updatedUrl, options);
    yield put(saveTransferBalanceSuccessAction(response));
    cb(response);
  } catch (err) {
    actions.setSubmitting(false);
    yield put(showToast(err));
  }
}

export function* getSettlementTimeline({
  data: { inProgressSettlements, cb },
}) {
  try {
    const { url, method } = SEARCH_VENDOR_SETTLEMENTS_TIMELINE_URL;
    const options = {
      headers: {
        'Content-type': 'application/json',
      },
      method,
      queryString:
        inProgressSettlements && inProgressSettlements.length
          ? { settlementIds: inProgressSettlements.join(',') }
          : {},
    };
    const response = yield call(request, url, options);
    if (!response) {
      cb({ error: 'Data not found' });
      return;
    }
    if (cb) {
      cb(response);
    }
  } catch (error) {
    yield put(showToast(error));
    if (cb) {
      cb({ error });
    }
  }
}

function* debouncegetTransferBalance() {
  yield debounce(2000, GET_TRANSFER_BALANCE, getTransferBalance);
}

function* getTransferChargesSaga() {
  yield takeLatest(GET_TRANSFER_CHARGES, getTransferCharges);
}

function* getOnDemandTableDataSaga() {
  yield takeLatest(GET_ON_DEMAND_TABLE_DATA, getOnDemandTableData);
}

function* getScheduledTableDataSaga() {
  yield takeLatest(GET_SCHEDULED_TABLE_DATA, getScheduledTableData);
}

export function* generateSettlementReportSaga() {
  yield takeLatest(GENERATE_SETTLEMENT_REPORT, generateSettlementReport);
}

export function* createVendorBalanceTransferSaga() {
  yield takeLatest(CREATE_VENDOR_BALANCE_TRANSFER, createVendorBalanceTransfer);
}

export function* getSettlementTimelineSaga() {
  yield takeLatest(GET_SETTLEMENT_TIMELINE, getSettlementTimeline);
}

// Individual exports for testing
export default function* easySplitPaymentsSaga() {
  const sagas = [
    debouncegetTransferBalance,
    getTransferChargesSaga,
    getOnDemandTableDataSaga,
    getScheduledTableDataSaga,
    generateSettlementReportSaga,
    createVendorBalanceTransferSaga,
    getSettlementTimelineSaga,
  ];

  yield all(
    sagas.map(saga =>
      // eslint-disable-next-line func-names
      spawn(function*() {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }
      }),
    ),
  );
}
